import React from "react";
import {
  MailIcon,
  PieChart,
  Users,
  FileCheck,
  Shield,
  Headphones,
  Building2,
  LineChart,
} from "lucide-react";
import { BRAND_THEME } from "../ui/brandTheme";

const SUPPORT_EMAIL = "kornivus.infotech@gmail.com";
const KORNIVUS_LOGO = "/login-logo.png";

const services = [
  {
    title: "HR & workforce management",
    description:
      "Employee records, roles, coverage areas, and onboarding workflows tailored for home health and clinical teams.",
    icon: Users,
  },
  {
    title: "Compliance & credentials",
    description:
      "Document tracking, license reminders, and alignment with agency and regulatory expectations.",
    icon: FileCheck,
  },
  {
    title: "Operations & reporting",
    description:
      "Dashboards, evaluations, payroll-related views, and exports to support leadership decisions.",
    icon: PieChart,
  },
  {
    title: "Security & access",
    description:
      "Role-based access, secure sessions, and structured permissions across modules.",
    icon: Shield,
  },
];

const overviewStats = [
  { label: "Support channels", value: "Email", sub: "Direct responses to your inbox" },
  { label: "Coverage", value: "All modules", sub: "HRMS platform scope" },
  { label: "Response focus", value: "Business hours", sub: "Priority for production issues" },
  { label: "Platform areas", value: "12+ workflows", sub: "Documents, billing, forms & more" },
];

const platformHighlights = [
  {
    title: "Dashboard & analytics",
    text: "Central visibility into activity across employees, applications, and operational touchpoints.",
    icon: LineChart,
  },
  {
    title: "Project-aligned delivery",
    text: "Features are grouped to match how agencies run intake, field staff, and back-office support.",
    icon: Building2,
  },
  {
    title: "Dedicated assistance",
    text: "Reach our team for configuration questions, access issues, and guidance on using the system effectively.",
    icon: Headphones,
  },
];

/** Page chrome — brand (Patient indigo) tokens so Support matches the app. */
const CARD = `bg-white rounded-xl border p-5 sm:p-6 shadow-sm ${BRAND_THEME.section.wrap}`;
const EYEBROW = `text-[11px] font-semibold uppercase tracking-[0.2em] ${BRAND_THEME.section.title}`;
const SECTION_TITLE = "text-xl sm:text-2xl font-semibold text-slate-900";
const SECTION_SUB = "text-sm text-slate-600 mt-1";
/** Icon tile used by the contact block and the service cards. */
const ICON_TILE =
  "shrink-0 flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100 shadow-sm";
const CTA = `inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold rounded-lg transition-colors shrink-0 ${BRAND_THEME.button.primary}`;

const Support = () => {
  return (
    /* Bleed into app content padding so the page uses full width; subtle bg vs white cards */
    <div className="min-h-full w-full -mx-6 px-4 sm:px-5 lg:px-8 pb-8 bg-slate-50 text-slate-800">
      <div className="w-full max-w-[100%] space-y-8 sm:space-y-10 pt-2">
        {/* Hero: logo + story + contact in one card */}
        <section className={`${CARD} p-0 overflow-hidden`}>
          <div className="grid lg:grid-cols-12 gap-0">
            <div className="lg:col-span-7 p-6 sm:p-8 lg:border-r border-indigo-100">
              <p className={`${EYEBROW} mb-4`}>
                Help &amp; support
              </p>
              <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 items-start">
                <div className="shrink-0 mx-auto sm:mx-0">
                  <div className="inline-flex items-center justify-center rounded-xl bg-white border border-indigo-100 px-6 py-5 shadow-inner">
                    <img
                      src={KORNIVUS_LOGO}
                      alt="Kornivus Infotech"
                      className="h-[72px] sm:h-20 w-auto max-w-[240px] object-contain"
                    />
                  </div>
                </div>
                <div className="min-w-0 flex-1 text-center sm:text-left">
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                    Kornivus Infotech
                  </h1>
                  <p className="mt-3 text-slate-600 text-sm sm:text-[15px] leading-relaxed">
                    Professional technology partner for health workforce and agency operations. We provide this
                    platform and ongoing assistance so your team can focus on patient care and compliance.
                  </p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-5 bg-indigo-50/40 p-6 sm:p-8 border-t lg:border-t-0 border-indigo-100">
              <p className={`${EYEBROW} mb-4`}>
                Contact us
              </p>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="flex flex-col sm:flex-row sm:items-start gap-4 group rounded-lg"
              >
                <span className={`${ICON_TILE} h-12 w-12 mx-auto sm:mx-0`}>
                  <MailIcon className="w-6 h-6" />
                </span>
                <div className="min-w-0 flex-1 text-center sm:text-left">
                  <span className="block text-sm font-medium text-slate-500 mb-1">Support email</span>
                  <span className="text-base sm:text-lg font-semibold text-indigo-800 break-all group-hover:underline underline-offset-2">
                    {SUPPORT_EMAIL}
                  </span>
                  <p className="mt-3 text-xs text-slate-600 leading-relaxed">
                    Account access, billing, and technical help. Please include your agency name and role.
                  </p>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Overview */}
        <section>
          <div className="mb-5">
            <h2 className={SECTION_TITLE}>Support overview</h2>
            <p className={SECTION_SUB}>
              At-a-glance scope of how we support your organization on this platform.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {overviewStats.map((row) => (
              <div key={row.label} className={CARD}>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700 mb-2">
                  {row.label}
                </p>
                <p className="text-lg font-semibold text-slate-900">{row.value}</p>
                <p className="text-xs text-slate-600 mt-3 border-t border-indigo-100 pt-3">{row.sub}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Services */}
        <section>
          <h2 className={`${SECTION_TITLE} mb-2`}>Services we provide</h2>
          <p className="text-sm text-slate-600 mb-6">
            Kornivus delivers software and services oriented to home health and clinical staffing: from HR
            workflows to documentation, billing alignment, and operational reporting.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
            {services.map(({ title, description, icon: Icon }) => (
              <div key={title} className={`${CARD} flex gap-4`}>
                <div className={`${ICON_TILE} w-12 h-12`}>
                  <Icon className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900">{title}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Platform */}
        <section className={`${CARD} p-0 overflow-hidden`}>
          <div className={`px-5 sm:px-6 py-4 border-b ${BRAND_THEME.section.head}`}>
            <h2 className={`text-lg font-semibold ${BRAND_THEME.section.title}`}>
              Platform &amp; project alignment
            </h2>
            <p className="text-xs text-slate-600 mt-1">
              How the product is organized to match agency programs and ongoing improvement.
            </p>
          </div>
          <div className="p-5 sm:p-6 grid grid-cols-1 md:grid-cols-3 gap-8 divide-y md:divide-y-0 md:divide-x divide-indigo-100">
            {platformHighlights.map(({ title, text, icon: Icon }) => (
              <div key={title} className="md:px-3 first:pl-0 last:pr-0 pt-6 md:pt-0 first:pt-0">
                <Icon className="w-8 h-8 text-indigo-500 mb-3" strokeWidth={1.25} />
                <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Footer CTA */}
        <section className={CARD}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <p className={EYEBROW}>
                Need help?
              </p>
              <p className="text-lg font-semibold text-slate-900 mt-2">We are here to help your agency succeed.</p>
              <p className="text-sm text-slate-600 mt-2 max-w-2xl leading-relaxed">
                Share screenshots, user role, and steps to reproduce for the fastest resolution. Support is provided
                by email only.
              </p>
            </div>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className={CTA}
            >
              <MailIcon className="w-4 h-4" />
              Send email
            </a>
          </div>
        </section>

        <p className="text-center text-xs text-slate-500 pb-2">
          © {new Date().getFullYear()} Kornivus Infotech. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Support;
